import type { DiskFileDto, DiskFolderDto } from './wikiApi';

/**
 * Чистые помощники по дереву папок Диска — общие для страницы «Диск» и окна
 * «Файл с Диска» в редакторе статьи (покрыты diskFolders.test.ts).
 */

/**
 * Суммарное число файлов в папке с учётом всех вложенных подпапок.
 * В сайдбаре показываем именно его — иначе родитель «Открытие точки» висел
 * с «0», хотя в подпапке «Проектирование» лежит 13 файлов.
 */
export function aggregateFileCounts(folders: DiskFolderDto[]): Map<string, number> {
  const childrenByParent = new Map<string | null, DiskFolderDto[]>();
  for (const f of folders) {
    const k = f.parentId ?? null;
    const arr = childrenByParent.get(k);
    if (arr) arr.push(f);
    else childrenByParent.set(k, [f]);
  }
  const memo = new Map<string, number>();
  const total = (f: DiskFolderDto): number => {
    const cached = memo.get(f.id);
    if (cached !== undefined) return cached;
    let sum = f.fileCount;
    for (const child of childrenByParent.get(f.id) ?? []) sum += total(child);
    memo.set(f.id, sum);
    return sum;
  };
  for (const f of folders) total(f);
  return memo;
}

/** Путь от корня до папки (для хлебных крошек). */
export function folderPath(folders: DiskFolderDto[], id: string | null): DiskFolderDto[] {
  if (!id) return [];
  const byId = new Map(folders.map((f) => [f.id, f]));
  const path: DiskFolderDto[] = [];
  let cur = byId.get(id) ?? null;
  // Защита от случайных циклов (не должно быть — guard на бэке).
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    path.unshift(cur);
    cur = cur.parentId ? byId.get(cur.parentId) ?? null : null;
  }
  return path;
}

/** Файл в результатах поиска по всему Диску: сам файл + путь папок «Раздел › Подраздел». */
export interface DiskSearchHit {
  file: DiskFileDto;
  path: string;
}

/**
 * Поиск файла по имени среди файлов всех папок (без учёта регистра, по всем словам запроса).
 * Короткий запрос (< 2 символов) ничего не ищет. Сортировка: имя начинается с запроса, затем по имени.
 */
export function searchDiskFiles(
  query: string,
  filesByFolder: ReadonlyArray<{ folderId: string; files: ReadonlyArray<DiskFileDto> }>,
  folders: DiskFolderDto[],
  limit = 100,
): DiskSearchHit[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const words = q.split(/\s+/).filter(Boolean);
  const hits: DiskSearchHit[] = [];
  for (const group of filesByFolder) {
    for (const file of group.files) {
      const name = file.fileName.toLowerCase();
      if (!words.every((w) => name.includes(w))) continue;
      hits.push({ file, path: folderPath(folders, group.folderId).map((f) => f.name).join(' › ') });
    }
  }
  const starts = (h: DiskSearchHit): number => (h.file.fileName.toLowerCase().startsWith(q) ? 0 : 1);
  hits.sort((a, b) => starts(a) - starts(b) || a.file.fileName.localeCompare(b.file.fileName, 'ru'));
  return hits.slice(0, limit);
}
